import { useEffect, useMemo, useRef, useState } from 'react'
import type { PipelineCatalogEntry } from 'app/components/action-center/usePipelinesCatalog'
import { useListCopy } from 'app/hooks/useListCopy'
import { moveListItem } from 'app/lib/listApi'
import { notifyError } from 'app/services/notify.client'
import { useContactStore, useSystemListStore } from 'app/stores/StoreContext'
import type { ContactJson } from 'app/types/entities'
import type { StatusListRow } from 'app/types/list-memberships'
import { useWorkspace } from 'app/utils'
import { listCatalogEntry, shownStageName, stageIdForName } from 'lib/listMemberships'
import { logError } from 'lib/logging'
import { legacyPipelineIdOf, SYSTEM_LISTS, systemListGate, systemListId } from 'lib/systemLists'

export type StatusListMemberships = {
  rows: StatusListRow[]
  catalog: PipelineCatalogEntry[]
  entries: { id: string; stage: string | undefined }[]
  replacing: Readonly<Record<string, string>>
  isStatusListId: (id: string) => boolean
  changeStage: (listId: string, stageName: string) => Promise<void>
}

// Active Leads' status has its own control on every surface, so only the other status lists are edited here.
const EDITED_HERE = SYSTEM_LISTS.filter((def) => def.statusField && def.listKind !== `leads`)

export function useStatusListMemberships(contact: ContactJson | undefined): StatusListMemberships {
  const store = useSystemListStore()
  const contactStore = useContactStore()
  const copy = useListCopy()
  const workspaceId = useWorkspace()?.id
  const [pending, setPending] = useState<Record<string, string>>({})
  // The newest move per contact and list, so a slower older one cannot overwrite it when it lands.
  const latestMove = useRef<Record<string, string>>({})

  // No contact means the surface has the grouped navigation off: nothing is read.
  // A workspace with no practice system has no treatment statuses, so the integration lock adds nothing here.
  const hasContact = !!contact
  const defs = useMemo(
    () =>
      workspaceId && hasContact
        ? EDITED_HERE.filter((def) => systemListGate(def, { hasWorkspace: true, treatmentGate: `available` }).if)
        : [],
    [workspaceId, hasContact],
  )

  useEffect(() => {
    for (const def of defs) store.ensure(systemListId(def, workspaceId!))
  }, [defs, store, workspaceId])

  const rows = defs.flatMap((def): StatusListRow[] => {
    const listId = systemListId(def, workspaceId!)
    const list = store.get(listId)
    if (!list) return []
    const recorded = contact?.[def.statusField!] ?? undefined
    const pendingKey = `${contact?.id}|${listId}`
    const status = pendingKey in pending ? pending[pendingKey] : recorded
    // The sync can record a status the list has no stage for (Completed, or Scheduled while the list has no such stage): that contact is off the list.
    return [
      {
        def,
        listId,
        catalogEntry: listCatalogEntry(list, def.bookedStage),
        stage: status && stageIdForName(list, status) ? status : undefined,
        defaultStage: shownStageName(list, undefined),
      },
    ]
  })

  // The contact the surface holds catches up after a refetch; from then on it is the truth again.
  useEffect(() => {
    setPending((current) => {
      const next = { ...current }
      for (const def of defs) {
        const pendingKey = `${contact?.id}|${systemListId(def, workspaceId!)}`
        if (pendingKey in next && contact?.[def.statusField!] === next[pendingKey]) delete next[pendingKey]
      }
      return Object.keys(next).length === Object.keys(current).length ? current : next
    })
  }, [contact, defs, workspaceId, pending])

  const changeStage = async (listId: string, stageName: string) => {
    const row = rows.find((candidate) => candidate.listId === listId)
    const list = store.get(listId)
    const stageId = list ? stageIdForName(list, stageName) : undefined
    if (!row || !stageId || !contact?.id) return
    // A booking sets the booked stage, so staff move no one into or out of it.
    const { lockedStage } = row.catalogEntry
    if (lockedStage && (stageName === lockedStage || row.stage === lockedStage)) return
    const field = row.def.statusField!
    const pendingKey = `${contact.id}|${listId}`
    latestMove.current[pendingKey] = stageName
    setPending((current) => ({ ...current, [pendingKey]: stageName }))
    try {
      await moveListItem(listId, contact.id, stageId)
      if (latestMove.current[pendingKey] === stageName) contactStore.updateInList(contact.id, { [field]: stageName })
    } catch (error) {
      logError(`Moving a contact between system list statuses failed`, error)
      // Drop this call's override so the recorded status shows through; a newer move's override stays.
      setPending((current) => {
        if (current[pendingKey] !== stageName) return current
        const { [pendingKey]: _failed, ...rest } = current
        return rest
      })
      notifyError(copy.failedToUpdateList, `pipeline-update-error`)
    }
  }

  // Built from the defs, not the loaded rows: the old pipeline entry must stay hidden while the record loads or fails to.
  const replacing = Object.fromEntries(
    defs.flatMap((def) => {
      const legacy = legacyPipelineIdOf(def)
      return legacy ? [[legacy, systemListId(def, workspaceId!)]] : []
    }),
  )

  return {
    rows,
    catalog: rows.map((row) => row.catalogEntry),
    entries: rows.filter((row) => row.stage).map((row) => ({ id: row.listId, stage: row.stage })),
    replacing,
    isStatusListId: (id) => rows.some((row) => row.listId === id),
    changeStage,
  }
}
